import React, { useState, useEffect, useRef, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import JoditProEditor from 'jodit-pro-react';
import { useImageUpload } from '../../hooks/useImageUpload';
import FileBrowser from './FileBrowser';

const NotesForm = forwardRef(({
  notesContent,
  setNotesContent,
  notesTitle,
  setNotesTitle,
  notesTime,
  setNotesTime,
  notesStatus,
  setNotesStatus,
  notesSequence,
  setNotesSequence,
  notesLevel,
  setNotesLevel,
  currentFile,
  setCurrentFile,
  DisplayFiles,
  isLoading,
  selectedSubTopic,
  selectedSubTopicName,
  handleNotesLevelChange
}, ref) => {
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const editor = useRef(null);
  const joditInstance = useRef(null);

  // Use the new image upload hook
  const { uploadState, clearError, clearSuccess } = useImageUpload();

  // getCurrentContent: read from editor instance (source of truth), like HTMLForm reads from files
  useImperativeHandle(ref, () => ({
    getCurrentContent: () => {
      try {
        const inst = joditInstance.current || editor.current;
        if (inst) {
          const val = inst.value ?? inst.jodit?.value;
          if (val != null) return String(val);
        }
        return notesContent || '';
      } catch (error) {
        return notesContent || '';
      }
    }
  }), [notesContent]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (uploadState.error || uploadState.success) {
      const timer = setTimeout(() => {
        clearError();
        clearSuccess();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [uploadState.error, uploadState.success, clearError, clearSuccess]);

  // Same as HTMLForm updateCurrentFileContent: update parent state immediately on every change
  const handleEditorChange = useCallback((content) => {
    if (content !== undefined && content !== null) {
      setNotesContent(String(content));
    }
  }, [setNotesContent]);

  const handleEditorBlur = useCallback((content) => {
    if (content !== undefined && content !== null) {
      setNotesContent(String(content));
    }
  }, [setNotesContent]);

  // Editor ready handler
  const handleEditorReady = useCallback(() => {
    setIsEditorReady(true);
  }, []);

  // Sync only when loading note (notesContent longer = external load). Never overwrite while typing.
  useEffect(() => {
    if (!isEditorReady || !notesContent || !editor.current) return;
    const timeoutId = setTimeout(() => {
      try {
        const editorVal = editor.current?.value || '';
        const notesVal = notesContent || '';
        if (editorVal !== notesVal && notesVal.length >= editorVal.length) {
          editor.current.value = notesContent;
        }
      } catch (error) {
        // non-critical
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [notesContent, isEditorReady]);

  // Handle image selection from file browser
  const handleImageSelect = useCallback((imageUrl) => {
    if (!editor.current) return;
    
    try {
      if (editor.current.selection) {
        editor.current.selection.insertImage(imageUrl);
      } else {
        // Fallback: insert at the end
        const currentContent = editor.current.value || '';
        const newContent = currentContent + `<img src="${imageUrl}" alt="Selected image" style="max-width: 100%; height: auto;" />`;
        editor.current.value = newContent;
        setNotesContent(newContent);
      }
    } catch (error) {
      console.error('Error inserting image:', error);
    }
  }, [setNotesContent]);

  // Memoized Jodit Pro editor configuration with all features from the image
  const config = useMemo(() => ({
    license: '470CM-1R561-BSOP6-RRPCZ',
    readonly: false,
    height: 400,
    placeholder: "Start writing your notes content here...",
    buttons: [
      // Source code view
      "source",
      "|",
      // Text formatting
      "bold", "italic", "underline", "strikethrough",
      "|",
      // Lists
      "ul", "ol", "indent", "outdent",
      "|",
        // Media insertion
        "image",
      "video",
      "link",
      "|",
      // Text alignment
      "align",
      "|",
      // Undo/Redo
      "undo", "redo",
      "|",
      // Table
      "table",
      "|",
      // Font controls
      "font", "fontsize", "brush", "paragraph",
      "|",
      // Special features
      "cut", "copy", "paste", "selectall",
      "|",
      "hr", "eraser", "copyformat",
      "|",
      "fullsize",
      "|",
      // Preview and print
      "preview", "print"
    ],
    // Custom CSS styling for the editor content
    style: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      fontSize: "16px",
      lineHeight: "1.6",
      margin: "40px",
      marginTop: "0px",
      color: "#333",
      height: "calc(100% - 10px)",
      overflow: "auto"
    },
    // Custom CSS rules for editor content
    extraCSS: `
      .jodit-wysiwyg h1, .jodit-wysiwyg h2 {
        font-weight: 600;
        color: #2C3E50;
        margin-top: 30px;
      }
      .jodit-wysiwyg ul {
        margin: 10px 0 20px 20px;
      }
      .jodit-wysiwyg li {
        margin-bottom: 8px;
      }
      .jodit-wysiwyg code {
        background-color: #F4F4F4;
        padding: 4px 6px;
        font-family: Consolas, monospace;
        border-radius: 4px;
      }
      .jodit-wysiwyg table {
        border-collapse: collapse;
        width: 100%;
        margin: 20px 0;
      }
      .jodit-wysiwyg th, .jodit-wysiwyg td {
        border: 1px solid #ddd;
        padding: 12px;
        text-align: left;
      }
      .jodit-wysiwyg th {
        background-color: #F0F0F0;
      }
      .jodit-wysiwyg pre {
        background-color: #F8F8F8;
        padding: 12px;
        border-left: 3px solid #ccc;
        font-size: 15px;
        overflow-x: auto;
      }
      .jodit-wysiwyg sup {
        vertical-align: super;
        font-size: smaller;
      }
      .jodit-wysiwyg sub {
        vertical-align: sub;
        font-size: smaller;
      }
    `,
    spellcheck: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    disablePlugins: 'powered-by-jodit',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    // Paste behavior: properly render pasted HTML content
    defaultActionOnPaste: 'insert_as_html',
    pastePlainText: false,
    processPasteHTML: true,
    cleanHTML: {
      fillEmptyParagraph: false,
      removeEmptyElements: false,
      removeSpans: false,
      removeStyles: false,
      removeCommands: false,
      cleanOnPaste: true
    },
    allowTags: true,
    allowStyles: true,
    allowAttributes: true,
    // Image configuration
    image: {
      showBrowser: true
    },
      // Uploader configuration - Jodit Pro compatible
      uploader: {
        url: `${process.env.REACT_APP_API_BASE_URL}/api/content_creation/upload-image/`,
      data: { subtopic_name: selectedSubTopicName?.replace(/\s+/g, '_') },
      format: 'json',
      method: 'POST',
      insertImageAsBase64URI: false,
      timeout: 30000,
      // Jodit Pro response handlers
      isSuccess: function (resp) {
        return resp && resp.data && resp.data.path;
      },
      process: function (resp) {
        return {
          files: resp.data.path || [],
          path: resp.data.path || [],
          baseurl: '',
          error: resp.error,
          msg: resp.message
        };
      },
      defaultHandlerSuccess: function (data) {
        const _this = this.jodit || this;
        if (Array.isArray(data.path)) {
          data.path.forEach((file) => {
            _this.selection.insertImage(file.URL);
          });
        } else if (data.path && data.path.URL) {
          _this.selection.insertImage(data.path.URL);
        }
      },
      error: function (e) {
        this.message.error(e.getMessage(), 4000);
      },
      progress: function (percent) {
        // Upload progress tracking
      }
    },
    // Events - MINIMAL to avoid focus issues
    events: {
      beforePaste: function (clipboardData) {
        try {
          const html = clipboardData?.getData?.('text/html') || clipboardData?.html;
          if (html) {
            // Let Jodit process and render the HTML
            return {
              html: html,
              action: 'insert_as_html'
            };
          }
          return true; // Let Jodit handle other content types
        } catch (error) {
          console.error('Paste handler error:', error);
          return true;
        }
      },
      afterPaste: function () {
        try {
          if (this.value !== undefined) {
            handleEditorChange(this.value);
          }
        } catch (error) {
          console.error('[NotesForm] After paste error:', error);
        }
      },
      afterInit: function () {
        handleEditorReady();
        if (notesContent && this.value !== notesContent) {
          this.value = notesContent;
        }
      },
      afterImageUpload: function () {
        try {
          if (this.value !== undefined) {
            handleEditorChange(this.value);
          }
        } catch (error) {
          console.error('[NotesForm] After image upload error:', error);
        }
      },
      afterImageUploadError: function (error) {
        console.error('Image upload error:', error);
      },
      afterChange: function () {
        const _this = this.jodit || this;
        if (_this?.value !== undefined) {
          handleEditorChange(_this.value);
        }
      }
    }
  }), [handleEditorReady, selectedSubTopicName, handleEditorChange, notesContent]);

  // Handle subtopic changes - update uploader data
  useEffect(() => {
    if (!editor.current || !isEditorReady) return;
    
    try {
      if (editor.current.options?.uploader) {
        editor.current.options.uploader.data = { 
          subtopic_name: selectedSubTopicName?.replace(/\s+/g, '_') || '' 
        };
      }
    } catch (error) {
        // Error updating editor options for subtopic change
      }
    }, [selectedSubTopicName, isEditorReady]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (editor.current?.destruct) {
        try {
          editor.current.destruct();
        } catch (error) {
          // Editor cleanup error is normal during cleanup
        }
      }
    };
  }, []);

  return (
    <div className="container-fluid p-1">
      <form className="shadow px-2 py-2 rounded border" style={{backgroundColor: '#f7f7f766'}}>
        {/* Error and Success Messages */}
        {uploadState.error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {uploadState.error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={clearError}
            ></button>
          </div>
        )}
        
        {uploadState.success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            Image uploaded successfully!
            <button 
              type="button" 
              className="btn-close" 
              onClick={clearSuccess}
            ></button>
          </div>
        )}

        {/* Upload Progress */}
        {uploadState.isUploading && (
          <div className="alert alert-info" role="alert">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Uploading...</span>
              </div>
              Uploading image... {uploadState.progress}%
            </div>
            <div className="progress mt-2" style={{height: '4px'}}>
              <div 
                className="progress-bar" 
                role="progressbar" 
                style={{width: `${uploadState.progress}%`}}
                aria-valuenow={uploadState.progress} 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-md-4">
            <div className="mb-3">
              <label htmlFor="time" className="form-label">
                Time (minutes): <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                id="time"
                className="form-control custom-input"
                value={notesTime}
                onChange={(e) => setNotesTime(e.target.value)}
                placeholder="Enter time in minutes (e.g., 10)"
                min="0"
                required
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label htmlFor="status" className="form-label">
                Status: <span className="text-danger">*</span>
              </label>
              <select
                id="status"
                className="form-control custom-input"
                value={notesStatus}
                onChange={(e) => setNotesStatus(e.target.value)}
                required
              >
                <option value="active">Active</option>
                <option value="not active">Not Active</option>
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label htmlFor="notesSequence" className="form-label">
                Sequence: <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                id="notesSequence"
                className="form-control custom-input"
                value={notesSequence}
                onChange={(e) => setNotesSequence(e.target.value)}
                placeholder="Enter sequence number"
                min="1"
                required
              />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="notesTitle" className="form-label">
            Notes Title: <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="notesTitle"
            className="form-control custom-input"
            value={notesTitle}
            onChange={(e) => setNotesTitle(e.target.value)}
            placeholder="Enter notes title"
            required
          />
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">
            Notes Content: <span className="text-danger">*</span>
          </label>
            {/* File Browser Button */}
            <button
              type="button"
              onClick={() => setShowFileBrowser(true)}
              className="btn btn-outline-primary btn-sm"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
              title="Browse Images"
            >
              <span>📁</span>
              Browse Images
            </button>
          </div>
          
          

          <div style={{ border: '1px solid #ccc', borderRadius: '4px' }}>
            {!isEditorReady && (
              <div className="text-center p-3 text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Initializing Jodit Pro Editor...
              </div>
            )}
            <div style={{ position: 'relative' }}>
              {typeof JoditProEditor !== 'undefined' ? (
                <JoditProEditor
                  key={currentFile || 'new'}
                  ref={editor}
                  editorRef={(inst) => { joditInstance.current = inst; }}
                  value={notesContent || ''}
                  config={config}
                  onBlur={handleEditorBlur}
                  onChange={handleEditorChange}
                  onError={(error) => {
                    // Error will be handled by the upload hook
                    console.error('Jodit Editor Error:', error);
                  }}
                />
              ) : (
                <div className="alert alert-warning">
                  Jodit Pro Editor is not available. Please check your license and refresh the page.
                </div>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* File Browser Popup */}
      <FileBrowser
        isOpen={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
        onImageSelect={handleImageSelect}
        selectedSubTopic={selectedSubTopic}
      />
    </div>
  );
});

export default NotesForm;
